import '../styles.scss';

import PropTypes from 'prop-types';
import React from 'react';
import UploadRequest from '../request';
import UploadButton from '../stateless/uploadButton';
import UploadDropZone from '../stateless/uploadDropZone';
import { useTranslation } from 'react-i18next';

// Wait for upload and call next
async function wait(upload, next) {
    try {
        const key = await upload;
        next(key);
    } catch (e) {
        next(null);
        throw e;
    }
}

// a noop callback for onFileSelected
const noop = () => null;

/**
 * A Upload button container. When a file is selected, an upload authorization
 * is requested on the `authorizationURL`. The authorization is used to upload
 * the file via a second request on an URL provided by the response of the
 * authorization request.
 */
export default function UploadButtonController({ authorizationURL, onFileSelected, onFileUploaded, dropzone, currentFile, fetch, label, labelModify, ...props }) {
    const { t } = useTranslation();
    const [isUploading, setUploading] = React.useState(false);
    onFileSelected = onFileSelected || noop;

    const label_ = currentFile ? labelModify || t(['ticket.common.action.edit', 'Select another file']) : label || t(['nacre.UploadButton.label', 'Select a file']);

    const handleFileSelected = React.useCallback(
        file => {
            const [_file] = file;
            onFileSelected(_file);

            const uploadRequest = new UploadRequest(authorizationURL, { fetch });
            setUploading(true);

            return wait(uploadRequest.uploadFile(_file), key => {
                setUploading(false);
                onFileUploaded(key);
            });
        },
        [authorizationURL, fetch, setUploading, onFileSelected, onFileUploaded]
    );

    const RenderComponent = dropzone && !currentFile ? UploadDropZone : UploadButton;
    return <RenderComponent onFileSelected={handleFileSelected} isUploading={isUploading} currentFile={currentFile} label={label_} {...props} />;
}

UploadButtonController.propTypes = {
    authorizationURL: PropTypes.string.isRequired,
    onFileUploaded: PropTypes.func.isRequired,
    icon: PropTypes.string,
    dropzone: PropTypes.bool,
    fetch: PropTypes.func,
    currentFile: PropTypes.any,
    onFileSelected: PropTypes.func,
    label: PropTypes.node,
    labelModify: PropTypes.node
};

UploadButtonController.defaultProps = {
    currentFile: null,
    icon: 'cloud upload',
    dropzone: true
};
